import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Heart, Minus, Plus, Truck, Gift, ShieldCheck } from "lucide-react";
import { api, cad, imgUrl } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { ProductCard } from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [active, setActive] = useState(0);
  const [qty, setQty] = useState(1);
  const [related, setRelated] = useState([]);
  const { add } = useCart();
  const { user, wishlist, toggleWishlist } = useAuth();

  useEffect(() => {
    window.scrollTo(0, 0);
    setQty(1);
    setActive(0);
    api.get(`/products/${id}`).then(({ data }) => {
      setProduct(data);
      api.get("/products", { params: { category: data.category } }).then(({ data: rel }) =>
        setRelated(rel.filter((p) => p.id !== id).slice(0, 4))
      );
    });
  }, [id]);

  if (!product) return <Layout><div className="py-40 text-center text-charcoal/50">Loading…</div></Layout>;

  const soldOut = (product.stock || 0) <= 0;
  const inWishlist = wishlist.includes(product.id);
  const images = product.images || [];

  const onAdd = () => {
    add(product, qty);
    toast.success(`${product.name} added to cart`);
  };

  const onWishlist = async () => {
    if (!user) return toast.error("Please sign in to save favourites");
    const added = await toggleWishlist(product.id);
    toast.success(added ? "Added to wishlist" : "Removed from wishlist");
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-10">
        <nav className="text-xs text-charcoal/50 mb-8">
          <Link to="/" className="hover:text-gold">Home</Link> /{" "}
          <Link to={`/shop?category=${product.category}`} className="hover:text-gold">{product.category}</Link> /{" "}
          <span className="text-charcoal/70">{product.name}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          <div>
            <div className="aspect-[4/5] overflow-hidden rounded-sm bg-blush border border-soft-border">
              {images[active] && (
                <img
                  data-testid="product-main-image"
                  src={imgUrl(images[active])}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-3 mt-4">
                {images.map((im, i) => (
                  <button
                    key={i}
                    data-testid={`thumb-${i}`}
                    onClick={() => setActive(i)}
                    className={`h-20 w-16 overflow-hidden rounded-sm border-2 transition-colors ${
                      active === i ? "border-gold" : "border-transparent"
                    }`}
                  >
                    <img src={imgUrl(im)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="eyebrow">{product.category}</p>
            <h1 className="mt-3 font-serif text-3xl lg:text-4xl text-charcoal">{product.name}</h1>
            <p className="mt-4 text-2xl text-charcoal">{cad(product.price)}</p>
            {(product.badges || []).length > 0 && (
              <div className="flex gap-2 mt-4">
                {product.badges.map((b) => (
                  <span key={b} className="eyebrow bg-blush px-3 py-1 rounded-full">{b}</span>
                ))}
              </div>
            )}
            <p className="mt-6 text-charcoal/70 leading-relaxed">{product.description}</p>

            <div className="mt-6">
              {soldOut ? (
                <div>
                  <span className="eyebrow text-destructive" data-testid="sold-out-label">Currently Sold Out</span>
                  <NotifyBackInStock productId={product.id} />
                </div>
              ) : (
                <span className="text-sm text-charcoal/60">{product.stock} in stock</span>
              )}
            </div>

            {!soldOut && (
              <div className="mt-6 flex items-center gap-4">
                <div className="inline-flex items-center border border-soft-border rounded-full">
                  <button data-testid="qty-dec" onClick={() => setQty(Math.max(1, qty - 1))} className="h-11 w-11 grid place-items-center">
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="w-10 text-center" data-testid="qty-value">{qty}</span>
                  <button data-testid="qty-inc" onClick={() => setQty(Math.min(product.stock, qty + 1))} className="h-11 w-11 grid place-items-center">
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            <div className="mt-6 flex gap-3">
              <Button
                data-testid="add-to-cart-btn"
                disabled={soldOut}
                onClick={onAdd}
                className="flex-1 h-13 py-6 bg-charcoal text-white rounded-none hover:bg-gold font-display tracking-wide disabled:opacity-40"
              >
                {soldOut ? "Sold Out" : "Add to Cart"}
              </Button>
              <Button
                data-testid="pdp-wishlist-btn"
                variant="outline"
                onClick={onWishlist}
                className="h-13 py-6 px-5 rounded-none border-charcoal/30 hover:border-gold"
              >
                <Heart className={`h-5 w-5 ${inWishlist ? "fill-gold text-gold" : "text-charcoal"}`} />
              </Button>
            </div>

            <div className="mt-8 space-y-3 border-t border-soft-border pt-6 text-sm text-charcoal/70">
              <div className="flex items-center gap-3"><Truck className="h-4 w-4 text-gold" /> Free shipping in Canada over $100 CAD</div>
              <div className="flex items-center gap-3"><Gift className="h-4 w-4 text-gold" /> Optional hand gift-wrapping at checkout</div>
              <div className="flex items-center gap-3"><ShieldCheck className="h-4 w-4 text-gold" /> Secure card payment via Stripe</div>
            </div>
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-24">
            <h2 className="font-serif text-2xl lg:text-3xl text-charcoal mb-8">You may also love</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-8">
              {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}


function NotifyBackInStock({ productId }) {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (user?.email) setEmail(user.email);
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return toast.error("Please enter your email");
    try {
      await api.post(`/products/${productId}/notify`, { email });
      setSent(true);
      toast.success("We'll email you the moment it's back 🌱");
    } catch {
      toast.error("Please enter a valid email");
    }
  };

  if (sent)
    return (
      <p className="mt-4 text-sm text-gold" data-testid="notify-confirmation">
        ✓ You're on the list — we'll let you know when it's back.
      </p>
    );

  return (
    <form onSubmit={submit} className="mt-4">
      <p className="text-sm text-charcoal/70 mb-2">Loved it? Get an email when it's back in stock.</p>
      <div className="flex gap-2 max-w-sm">
        <Input
          data-testid="notify-email-input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          className="h-11 rounded-none border-charcoal/20 bg-white"
        />
        <Button
          type="submit"
          data-testid="notify-submit-btn"
          className="h-11 rounded-none bg-charcoal text-white hover:bg-gold font-display tracking-wide"
        >
          Notify Me
        </Button>
      </div>
    </form>
  );
}
